import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion } from 'framer-motion';
import { formatDate, formatDateTime } from '../utils/dateFormat';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import axios from 'axios';
import { toast } from 'sonner';
function AdminApplications() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [applications, setApplications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');
  const [updateStatus, setUpdateStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [sendNotification, setSendNotification] = useState(true);
  const [updating, setUpdating] = useState(false);
  
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    
    if (user && user.role !== 'admin') {
      navigate('/dashboard');
      return;
    }
    
    if (user) {
      fetchApplications();
      fetchStats();
    }
  }, [user, authLoading, filterStatus]);
  
  const fetchApplications = async () => {
    try {
      const params = filterStatus !== 'all' ? `?status=${filterStatus}` : '';
      const response = await axios.get(`/api/admin/applications${params}`);
      setApplications(response.data.applications || []);
    } catch (error) {
      toast.error('Failed to fetch applications');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchStats = async () => {
    try {
      const response = await axios.get('/api/admin/applications/stats');
      setStats(response.data.stats);
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };
  
  const fetchApplicationDetail = async (applicationId) => {
    setDetailLoading(true);
    try {
      const response = await axios.get(`/api/admin/applications/${applicationId}`);
      setSelectedApplication(response.data.application);
      setUpdateStatus(response.data.application.status);
      setAdminNotes(response.data.application.adminNotes || '');
    } catch (error) {
      toast.error('Failed to fetch application details');
    } finally {
      setDetailLoading(false);
    }
  };
  
  const handleStatusUpdate = async () => {
    if (!selectedApplication) return;
    
    setUpdating(true);
    try {
      await axios.put(`/api/admin/applications/${selectedApplication.id}/status`, {
        status: updateStatus,
        adminNotes: adminNotes,
        sendNotification: sendNotification
      });
      toast.success(`Application ${updateStatus}`);
      fetchApplications();
      fetchStats();
      setSelectedApplication(null);
    } catch (error) {
      toast.error('Failed to update application');
    } finally {
      setUpdating(false);
    }
  };
  
  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-amber-100 text-amber-700',
      under_review: 'bg-blue-100 text-blue-700',
      accepted: 'bg-emerald-100 text-emerald-700',
      rejected: 'bg-red-100 text-red-700',
      waitlisted: 'bg-violet-100 text-violet-700'
    };
    
    const labels = {
      pending: 'Pending',
      under_review: 'Under Review',
      accepted: 'Accepted',
      rejected: 'Rejected',
      waitlisted: 'Waitlisted'
    };
    
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getDomainLabel = (domain) => {
    const labels = {
      'engineering': 'Engineering / Technology',
      'product': 'Product Management',
      'design': 'Design / UX',
      'operations': 'Operations / COO',
      'finance': 'Finance / CFO',
      'sales': 'Sales / Revenue',
      'marketing': 'Marketing / Growth',
      'hr': 'People / HR',
      'strategy': 'Strategy / Consulting',
      'founder': 'Founder / CEO',
      'other': 'Other'
    };
    return labels[domain] || domain;
  };

  const getHowHeardLabel = (source) => {
    const labels = {
      'linkedin': 'LinkedIn',
      'twitter': 'Twitter/X',
      'referral': 'Personal Referral',
      'podcast': 'Podcast',
      'article': 'Article / Blog',
      'search': 'Search Engine',
      'event': 'Event / Conference',
      'other': 'Other'
    };
    return labels[source] || source;
  };
  
  if (authLoading || loading) {
    return (
        <div className="space-y-4">
          <Skeleton className="h-8 w-48" />
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
    );
  }
  
  return (
      <div>
        {/* Page Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-semibold">PATH Application Management</h1>
          <p className="text-muted-foreground">Review and manage programme applications</p>
        </div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-8"
        >
          {/* Stats Cards */}
          {stats && (
            <div className="grid grid-cols-2 md:grid-cols-6 gap-4">
              <div className="bg-card border border-border rounded-[var(--radius)] p-4">
                <p className="text-sm text-muted-foreground">Total</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
              <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded-[var(--radius)] p-4">
                <p className="text-sm text-amber-600 dark:text-amber-400">Pending</p>
                <p className="text-2xl font-semibold text-amber-700 dark:text-amber-300">{stats.pending}</p>
              </div>
              <div className="bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 rounded-[var(--radius)] p-4">
                <p className="text-sm text-blue-600 dark:text-blue-400">Under Review</p>
                <p className="text-2xl font-semibold text-blue-700 dark:text-blue-300">{stats.underReview}</p>
              </div>
              <div className="bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800 rounded-[var(--radius)] p-4">
                <p className="text-sm text-emerald-600 dark:text-emerald-400">Accepted</p>
                <p className="text-2xl font-semibold text-emerald-700 dark:text-emerald-300">{stats.accepted}</p>
              </div>
              <div className="bg-violet-50 dark:bg-violet-950/30 border border-violet-200 dark:border-violet-800 rounded-[var(--radius)] p-4">
                <p className="text-sm text-violet-600 dark:text-violet-400">Waitlisted</p>
                <p className="text-2xl font-semibold text-violet-700 dark:text-violet-300">{stats.waitlisted}</p>
              </div>
              <div className="bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-[var(--radius)] p-4">
                <p className="text-sm text-red-600 dark:text-red-400">Rejected</p>
                <p className="text-2xl font-semibold text-red-700 dark:text-red-300">{stats.rejected}</p>
              </div>
            </div>
          )}
          
          {/* Filter */}
          <div className="flex items-center gap-4">
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Applications</SelectItem>
                <SelectItem value="pending">Pending</SelectItem>
                <SelectItem value="under_review">Under Review</SelectItem>
                <SelectItem value="accepted">Accepted</SelectItem>
                <SelectItem value="waitlisted">Waitlisted</SelectItem>
                <SelectItem value="rejected">Rejected</SelectItem>
              </SelectContent>
            </Select>
            
            <span className="text-sm text-muted-foreground">
              {applications.length} application(s)
            </span>
          </div>
          
          {/* Applications Table */}
          <div className="bg-card border border-border rounded-[var(--radius)] overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-muted/50 border-b border-border">
                  <tr>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Applicant</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Role</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Domain</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Submitted</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Status</th>
                    <th className="text-left px-4 py-3 text-sm font-medium text-muted-foreground">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {applications.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        No applications found
                      </td>
                    </tr>
                  ) : (
                    applications.map((app) => (
                      <tr key={app.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                        <td className="px-4 py-4">
                          <div>
                            <p className="font-medium">
                              {app.participantInfo?.firstName} {app.participantInfo?.lastName}
                            </p>
                            <p className="text-sm text-muted-foreground">{app.participantInfo?.email}</p>
                          </div>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm">{app.currentRole}</p>
                          <p className="text-xs text-muted-foreground">{app.yearsExperience} years</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm">{getDomainLabel(app.primaryDomain)}</p>
                        </td>
                        <td className="px-4 py-4">
                          <p className="text-sm">{formatDate(app.submittedAt)}</p>
                        </td>
                        <td className="px-4 py-4">
                          {getStatusBadge(app.status)}
                        </td>
                        <td className="px-4 py-4">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => fetchApplicationDetail(app.id)}
                          >
                            Review
                          </Button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </motion.div>
      
      {/* Application Detail Modal */}
      <Dialog open={!!selectedApplication} onOpenChange={() => setSelectedApplication(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>PATH Application Review</DialogTitle>
            <DialogDescription>
              Review application details and update status
            </DialogDescription>
          </DialogHeader>
          
          {detailLoading ? (
            <div className="space-y-4 py-8">
              <Skeleton className="h-8 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
            </div>
          ) : selectedApplication && (
            <div className="space-y-6 mt-4">
              {/* Applicant Info */}
              <div className="grid grid-cols-2 gap-6 p-4 bg-muted/30 rounded-[var(--radius)]">
                <div>
                  <label className="text-sm text-muted-foreground">Applicant</label>
                  <p className="font-medium">
                    {selectedApplication.fullParticipantInfo?.firstName} {selectedApplication.fullParticipantInfo?.lastName}
                  </p>
                  <p className="text-sm text-muted-foreground">{selectedApplication.fullParticipantInfo?.email}</p>
                  {selectedApplication.linkedInProfile && (
                    <a 
                      href={selectedApplication.linkedInProfile.startsWith('http') ? selectedApplication.linkedInProfile : `https://${selectedApplication.linkedInProfile}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-primary hover:underline"
                    >
                      LinkedIn Profile →
                    </a>
                  )}
                </div>
                <div>
                  <label className="text-sm text-muted-foreground">Professional Background</label>
                  <p className="font-medium">{selectedApplication.currentRole}</p>
                  <p className="text-sm text-muted-foreground">{selectedApplication.yearsExperience} years experience</p>
                  <p className="text-sm text-muted-foreground">{getDomainLabel(selectedApplication.primaryDomain)}</p>
                </div>
              </div>

              {/* How They Heard About PATH */}
              <div>
                <label className="text-sm text-muted-foreground mb-2 block">How They Heard About PATH</label>
                <p className="p-3 bg-muted/30 rounded-[var(--radius)]">{getHowHeardLabel(selectedApplication.howHeardAbout)}</p>
              </div>
              
              {/* Assessment Summary */}
              {selectedApplication.fullAssessments?.length > 0 && (
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Assessment Results</label>
                  <div className="grid gap-4">
                    {selectedApplication.fullAssessments.map((assessment, idx) => (
                      <div key={idx} className="p-4 border border-border rounded-[var(--radius)]">
                        {assessment.type === 'fear' && (
                          <>
                            <p className="font-medium text-amber-700 dark:text-amber-400 mb-2">EDGE Diagnostic</p>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Primary Pressure:</span>
                                <p className="font-medium">{assessment.results?.primary_fear_cluster}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Intensity:</span>
                                <p className="font-medium">{assessment.results?.primary_fear_intensity}</p>
                              </div>
                              {assessment.results?.root_fear && (
                                <div className="col-span-2">
                                  <span className="text-muted-foreground">Root Pressure:</span>
                                  <p>{assessment.results.root_fear}</p>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                        {assessment.type === 'archetype' && (
                          <>
                            <p className="font-medium text-violet-700 dark:text-violet-400 mb-2">CORE Diagnostic</p>
                            <div className="grid grid-cols-2 gap-4 text-sm">
                              <div>
                                <span className="text-muted-foreground">Stated Archetype:</span>
                                <p className="font-medium">{assessment.results?.primary_archetype}</p>
                              </div>
                              <div>
                                <span className="text-muted-foreground">Core Archetype:</span>
                                <p className="font-medium">{assessment.results?.secondary_archetype}</p>
                              </div>
                              {assessment.results?.conscious_unconscious_gap && (
                                <div className="col-span-2">
                                  <span className="text-amber-600 dark:text-amber-400 font-medium">⚠️ Stated-Core Gap Detected:</span>
                                  <p className="text-sm">{assessment.results.gap_description}</p>
                                </div>
                              )}
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Application Responses - New Fields */}
              <div className="space-y-4">
                <h4 className="font-semibold border-b border-border pb-2">Application Responses</h4>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Impact Without Authority</label>
                  <p className="p-3 bg-muted/30 rounded-[var(--radius)] whitespace-pre-wrap text-sm">
                    {selectedApplication.impactWithoutAuthority}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Most Commercially Meaningful Outcome</label>
                  <p className="p-3 bg-muted/30 rounded-[var(--radius)] whitespace-pre-wrap text-sm">
                    {selectedApplication.commercialOutcome}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Why PATH is Relevant Now</label>
                  <p className="p-3 bg-muted/30 rounded-[var(--radius)] whitespace-pre-wrap text-sm">
                    {selectedApplication.whyPathNow}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Success Vision (12 Months)</label>
                  <p className="p-3 bg-muted/30 rounded-[var(--radius)] whitespace-pre-wrap text-sm">
                    {selectedApplication.successVision}
                  </p>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Commitment Space</label>
                  <p className="p-3 bg-muted/30 rounded-[var(--radius)] whitespace-pre-wrap text-sm">
                    {selectedApplication.commitmentSpace}
                  </p>
                </div>
                
                {selectedApplication.additionalInfo && (
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Additional Information</label>
                    <p className="p-3 bg-muted/30 rounded-[var(--radius)] whitespace-pre-wrap text-sm">
                      {selectedApplication.additionalInfo}
                    </p>
                  </div>
                )}
              </div>
              
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  {selectedApplication.cohortCommitment ? '✅' : '❌'} Cohort commitment confirmed
                </div>
                <div className="flex items-center gap-2">
                  {selectedApplication.agreedToTerms ? '✅' : '❌'} Agreed to terms
                </div>
              </div>
              
              {/* Status Update Section */}
              <div className="border-t border-border pt-6 space-y-4">
                <h4 className="font-semibold">Update Application Status</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-muted-foreground mb-2 block">Status</label>
                    <Select value={updateStatus} onValueChange={setUpdateStatus}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pending">Pending</SelectItem>
                        <SelectItem value="under_review">Under Review</SelectItem>
                        <SelectItem value="accepted">Accepted</SelectItem>
                        <SelectItem value="waitlisted">Waitlisted</SelectItem>
                        <SelectItem value="rejected">Rejected</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div className="flex items-end">
                    <div className="flex items-center space-x-2">
                      <Checkbox
                        id="sendNotification"
                        checked={sendNotification}
                        onCheckedChange={setSendNotification}
                      />
                      <label htmlFor="sendNotification" className="text-sm">
                        Send email notification
                      </label>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className="text-sm text-muted-foreground mb-2 block">Admin Notes (internal)</label>
                  <Textarea
                    value={adminNotes}
                    onChange={(e) => setAdminNotes(e.target.value)}
                    placeholder="Add internal notes about this application..."
                    rows={3}
                  />
                </div>
                
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setSelectedApplication(null)}>
                    Cancel
                  </Button>
                  <Button
                    onClick={handleStatusUpdate}
                    disabled={updating}
                    className="bg-primary hover:bg-primary/90"
                  >
                    {updating ? 'Updating...' : 'Update Status'}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
  );
}

export default AdminApplications;
